import React from 'react';
import { CheckIcon, ShieldCheckIcon } from 'lucide-react';
import { useOffer } from '../contexts/OfferContext';
import { offerConfig } from '../data/siteConfig';
type Tone = 'dark' | 'light';
type FbqWindow = Window & {
  fbq?: (...args: unknown[]) => void;
};
export const CTAButton = ({
  children,
  href = '#precio',
  className = ''
}: {
  children: React.ReactNode;
  href?: string;
  className?: string;
}) => {
  const {
    price
  } = useOffer();
  const handleClick = () => {
    const fbq = (window as FbqWindow).fbq;
    if (fbq && (href.includes('checkout') || href.includes('/cart/'))) {
      fbq('track', 'InitiateCheckout', {
        content_name: 'Vitalidad 360 Master Kit',
        content_category: 'Health',
        currency: 'ARS',
        value: price
      });
    }
  };
  return <a href={href} onClick={handleClick} className={`cta-animated inline-flex items-center justify-center w-full md:w-auto min-h-[52px] px-5 md:px-8 py-3 md:py-4 rounded-lg bg-familia-cta hover:bg-familia-ctaHover text-familia-ctaInk font-bold uppercase text-center text-[16px] md:text-[18px] leading-tight md:shadow-[0_8px_20px_-10px_rgba(244,180,58,0.6)] transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-familia-dark ${className}`} data-id="element-204" data-mp="1vvjbki4nkj2a">
      {children}
    </a>;
};
export const CTAMicrocopy = ({
  tone = 'dark'
}: {
  tone?: Tone;
}) => <p className={`mt-3 text-[14px] sm:text-[15px] leading-snug text-center ${tone === 'dark' ? 'text-familia-onDarkMuted' : 'text-familia-inkMuted'}`} data-id="element-205" data-mp="2qn870h9wcthm">
    ⚡ Te llega al mail en minutos · 🛡️ Garantía de {offerConfig.guaranteeDays}{' '}
    días · 🔒 Mercado Pago
  </p>;
export const CTABlock = ({
  children,
  href,
  tone = 'dark',
  className = '',
  buttonClassName = ''
}: {
  children: React.ReactNode;
  href?: string;
  tone?: Tone;
  className?: string;
  buttonClassName?: string;
}) => <div className={`flex flex-col items-center w-full ${className}`} data-id="element-206" data-mp="22xnm3su6nm5p">
    <CTAButton href={href} className={buttonClassName} data-id="element-207" data-mp="1t75kqxm35g6p">
      {children}
    </CTAButton>
    <CTAMicrocopy tone={tone} data-id="element-208" data-mp="1y82zt1bstu3z" />
  </div>;
export const GuaranteeSeal = ({
  size = 'md'
}: {
  size?: 'md' | 'lg';
}) => {
  const dims = size === 'lg' ? 'w-36 h-36' : 'w-28 h-28';
  return <div role="img" aria-label={`Garantía de ${offerConfig.guaranteeDays} días`} className={`${dims} rounded-full bg-familia-gold text-familia-ctaInk flex flex-col items-center justify-center ring-2 ring-familia-gold/50 ring-offset-4 ring-offset-transparent`} data-id="element-209" data-mp="1o8c7n6apnr72">
      <ShieldCheckIcon className={size === 'lg' ? 'w-7 h-7 mb-0.5' : 'w-5 h-5 mb-0.5'} aria-hidden="true" data-id="element-210" data-mp="2gzdpjf5sg0ua" />
      <span className="text-[11px] font-bold tracking-[0.15em] leading-none" data-id="element-211" data-mp="277q35so399zo">
        GARANTÍA
      </span>
      <span className={`font-display font-bold leading-none ${size === 'lg' ? 'text-4xl' : 'text-3xl'}`} data-id="element-212" data-mp="2uxkf880l2uiv">
        {offerConfig.guaranteeDays}
      </span>
      <span className="text-[11px] font-bold tracking-[0.15em] leading-none" data-id="element-213" data-mp="2zplmffiw9v21">
        DÍAS
      </span>
    </div>;
};
const PRICE_ITEMS = ['5 módulos centrales', 'Módulo VIP con +150 ejercicios en 3 niveles', '3 bonos incluidos', 'Bitácoras y planillas de salud', 'Imprimible sin límite', 'Llega a tu mail en minutos', 'Pago único y acceso de por vida'];
export const PriceCard = () => {
  const {
    priceLabel,
    previousPriceLabel
  } = useOffer();
  return <div data-price-card className="bg-familia-darkCard border-2 border-familia-gold rounded-2xl p-6 sm:p-10 md:shadow-[0_16px_48px_-24px_rgba(0,0,0,0.5)] w-full max-w-2xl mx-auto relative overflow-hidden text-white text-left" data-id="element-214" data-mp="59o045y9eulb">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-familia-gold text-familia-ctaInk font-bold text-[13px] tracking-wider py-1 px-4 rounded-b-lg uppercase" data-id="element-215" data-mp="ebngqx5d3wnq">
        Kit completo
      </div>
      <h3 className="text-center text-2xl sm:text-3xl font-display font-bold mt-5 mb-7" data-id="element-216" data-mp="1pog661aok90m">
        Vitalidad 360 · Kit completo
      </h3>

      <div className="flex items-center gap-4 sm:gap-6 mb-7 bg-familia-dark/60 p-4 sm:p-5 rounded-xl border border-familia-darkBorder" data-id="element-217" data-mp="2gym1nyf5cnkw">
        <img src="https://cdn.magicpatterns.com/uploads/wD6XHHXqEzi2b4vbyPnFv3/hf_20260528_015615_d7bdc0e4-f184-4423-868f-2fe77bd4c3a9-removebg-preview.png" alt="Vitalidad 360 Master Kit" width="96" height="96" loading="lazy" decoding="async" className="w-20 h-20 sm:w-24 sm:h-24 object-contain shrink-0" data-id="element-218" data-mp="2kzfsp0nvyii0" />
        <div className="flex flex-col items-start min-w-0" data-id="element-219" data-mp="2zwf7p9l2hdmi">
          <span className="text-familia-onDarkMuted text-[14px] font-semibold mb-1" data-id="element-220" data-mp="33z1yr89wyuin">
            Precio de lanzamiento
          </span>
          <span className="text-familia-onDarkMuted text-[16px] line-through decoration-familia-urgent decoration-2 mb-1" data-id="element-221" data-mp="38qtk94e7qa1x">
            <span className="sr-only" data-id="element-222" data-mp="3do3gb2w3xjyq">Precio anterior: </span>
            {previousPriceLabel}
          </span>
          <span className="text-familia-accent text-5xl sm:text-6xl font-display font-bold leading-none" data-id="element-223" data-mp="3ilv7tvsub42e">
            <span className="sr-only" data-id="element-224" data-mp="3nkom3qfyaz3m">Precio actual: </span>
            {priceLabel}
          </span>
          <span className="text-familia-onDarkMuted text-[14px] sm:text-[15px] mt-3 leading-snug" data-id="element-225" data-mp="1bxcqkmrd8z0m">
            Pago único · Sin suscripción · Acceso de por vida
          </span>
        </div>
      </div>

      <CTABlock href={offerConfig.checkoutUrl} tone="dark" buttonClassName="md:w-full md:py-5" className="mb-8" data-id="element-226" data-mp="123cn0pbqg1u0">
        SÍ, QUIERO EL KIT HOY
      </CTABlock>

      <div className="flex justify-center mb-6" data-id="element-227" data-mp="1uovuqwx4mj48">
        <GuaranteeSeal data-id="element-228" data-mp="1pr44e7c4r525" />
      </div>
      <p className="text-center text-[14px] sm:text-[15px] text-familia-onDarkMuted mb-8 leading-snug" data-id="element-229" data-mp="1ps5di69l5hh8">
        🔒 Pago seguro con Mercado Pago · Tarjeta de crédito, débito o dinero
        en cuenta
      </p>

      <ul className="space-y-3 border-t border-familia-darkBorder pt-7" data-id="element-230" data-mp="1g0j2a9x8zieh">
        {PRICE_ITEMS.map(item => <li key={item} className="flex items-start gap-3" data-id="element-231" data-mp="2dhgenouuldvw">
            <CheckIcon className="w-5 h-5 text-familia-checkDark shrink-0 mt-1" strokeWidth={3} aria-hidden="true" data-id="element-232" data-mp="28pgblnjhe4v3" />
            <span className="text-body font-medium" data-id="element-233" data-mp="6s66hiaq1za6">{item}</span>
          </li>)}
      </ul>
    </div>;
};